/**
 * Mirrors server depenseHelper cash-register guards: for non-BANK caisses,
 * outgoing payment is limited by min(balance, state.montant* for the method).
 */

const METHOD_STATE_KEYS = {
  cash: "montantEspece",
  cheque: "montantCheque",
  traite: "montantTraite",
  virement: "montantVirement",
  prelevement: "montantVirement",
  tpe: "montantTPE",
};

function round3(n) {
  return Number(Number(n).toFixed(3));
}

function readMoney(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "object" && v != null && typeof v.toString === "function") {
    const x = Number(v.toString());
    return Number.isFinite(x) ? x : 0;
  }
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

/**
 * @param {{ myCaisses?: object[], depenseCashCaisses?: object[], otherCaisses?: object[] }} lookups
 * @param {string} id - Caisse _id
 * @returns {object|null}
 */
export function findCashCaisseInLookups(lookups, id) {
  if (!lookups || !id) return null;
  const sid = String(id);
  const lists = [
    ...(lookups.myCaisses || []),
    ...(lookups.depenseCashCaisses || []),
    ...(lookups.otherCaisses || []),
  ];
  return lists.find((c) => c && String(c._id) === sid) || null;
}

/**
 * @param {object} caisse - lean Caisse doc
 * @param {string} method - payment method (cash, cheque, …)
 * @returns {number|null} null if caisse missing
 */
export function availableDecaissementForMethod(caisse, method) {
  if (!caisse) return null;
  const bal = round3(readMoney(caisse.balance));
  if (caisse.type === "BANK") return bal;
  const m = String(method || "cash")
    .trim()
    .toLowerCase();
  const key = METHOD_STATE_KEYS[m];
  if (!key) return bal;
  const bucket = round3(readMoney((caisse.state || {})[key]));
  return round3(Math.min(bal, bucket));
}

/**
 * True when a cash split line's amount exceeds what that register can pay for the line's method.
 * @param {{ myCaisses?: object[], depenseCashCaisses?: object[], otherCaisses?: object[] }} lookups
 * @param {{ method?: string, caisseId?: string, amount?: number }} line
 */
export function cashLineAmountExceedsAvailability(lookups, line) {
  if (!line || String(line.method || "").toLowerCase() !== "cash") return false;
  if (!line.caisseId) return false;
  const amt = round3(Number(line.amount) || 0);
  if (amt <= 0) return false;
  const c = findCashCaisseInLookups(lookups, line.caisseId);
  const avail = availableDecaissementForMethod(c, line.method);
  if (avail == null) return false;
  return amt - avail > 0.0005;
}

/**
 * @param {{ caisseBanks?: object[] }} lookups
 * @param {string} id - BANK caisse _id
 * @returns {object|null}
 */
export function findBankCaisseInLookups(lookups, id) {
  if (!lookups || !id) return null;
  const sid = String(id);
  return (lookups.caisseBanks || []).find((b) => b && String(b._id) === sid) || null;
}

/**
 * True when a non-cash line debiting a bank caisse exceeds that account's balance.
 * @param {{ caisseBanks?: object[] }} lookups
 * @param {{ method?: string, caisseBankId?: string, amount?: number }} line
 */
export function bankLineAmountExceedsAvailability(lookups, line) {
  if (!line) return false;
  if (String(line.method || "").toLowerCase() === "cash") return false;
  if (!line.caisseBankId) return false;
  const amt = round3(Number(line.amount) || 0);
  if (amt <= 0) return false;
  const b = findBankCaisseInLookups(lookups, line.caisseBankId);
  const avail = availableDecaissementForMethod(b, line.method);
  if (avail == null) return false;
  return amt - avail > 0.0005;
}

/** True if the line's amount exceeds the applicable source (cash caisse or bank caisse). */
export function lineAmountExceedsPaymentSource(lookups, line) {
  return (
    cashLineAmountExceedsAvailability(lookups, line) ||
    bankLineAmountExceedsAvailability(lookups, line)
  );
}
